import { useMemo } from 'react'
import { Box, Breadcrumbs, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Alert } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { listProductionOrders, getExpiryRisk, getOverdueMaintenance, type ProductionOrderStatus } from '../../api/productieOrdine'

const STATUS_LABEL: Record<ProductionOrderStatus, string> = {
  Planificat: 'Planificat', Lansat: 'Lansat', InPregatire: 'În pregătire', InExecutie: 'În execuție',
  PartialFinalizat: 'Parțial finalizat', Finalizat: 'Finalizat', Inchis: 'Închis', Suspendat: 'Suspendat', Anulat: 'Anulat',
}

export default function PlanPage() {
  const { data: orders } = useQuery({ queryKey: ['productie', 'ordine'], queryFn: listProductionOrders })
  const { data: risk } = useQuery({ queryKey: ['productie', 'plan', 'risc-expirare'], queryFn: () => getExpiryRisk(30) })
  const { data: overdueMaintenance } = useQuery({ queryKey: ['productie', 'plan', 'mentenanta-restanta'], queryFn: getOverdueMaintenance })

  const byEquipment = useMemo(() => {
    const groups = new Map<string, typeof orders>()
    for (const o of orders ?? []) {
      if (o.status === 'Anulat' || o.status === 'Inchis') continue
      if (!groups.has(o.equipmentName)) groups.set(o.equipmentName, [])
      groups.get(o.equipmentName)!.push(o)
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))
  }, [orders])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Producție</Typography>
        <Typography variant="inherit" color="text.primary">Plan de producție</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Plan de producție</Typography>

      {risk && risk.length > 0 && (
        <Alert severity="warning">
          {risk.length} lot(uri) cu termen de valabilitate apropiat — valoare imobilizată totală{' '}
          {risk.reduce((s, r) => s + r.estimatedValueAtRisk, 0).toFixed(2).replace('.', ',')}.
        </Alert>
      )}

      {risk && risk.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Risc de expirare (FR-OPL-015/016)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Articol</TableCell><TableCell>Lot</TableCell><TableCell>Expiră la</TableCell>
                <TableCell align="right">Sold Liber</TableCell><TableCell align="right">Valoare imobilizată</TableCell>
                <TableCell align="right">OP-uri planificate</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {risk.map((r) => (
                <TableRow key={`${r.articleId}-${r.lotNumber}`}>
                  <TableCell>{r.articleCode} — {r.articleDescription}</TableCell>
                  <TableCell><code>{r.lotNumber}</code></TableCell>
                  <TableCell>{r.expiryDate}</TableCell>
                  <TableCell align="right">{r.liberQuantity}</TableCell>
                  <TableCell align="right">{r.estimatedValueAtRisk.toFixed(2).replace('.', ',')}</TableCell>
                  <TableCell align="right">{r.plannedOrdersUsingArticle}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {overdueMaintenance && overdueMaintenance.length > 0 && (
        <Alert severity="warning">
          {overdueMaintenance.length} echipament(e) cu mentenanță restantă sau apropiată (FR-MNT-008) —
          verificați fereastra înainte de a lansa producție pe ele.
        </Alert>
      )}

      {overdueMaintenance && overdueMaintenance.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Mentenanță restantă (FR-MNT-004…008)</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Echipament</TableCell><TableCell>Intervenție</TableCell><TableCell>Scadență</TableCell><TableCell>Stare</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overdueMaintenance.map((m) => (
                <TableRow key={m.scheduleId}>
                  <TableCell>{m.equipmentCode} — {m.equipmentName}</TableCell>
                  <TableCell>{m.interventionType}</TableCell>
                  <TableCell>{m.nextDueDate}</TableCell>
                  <TableCell>
                    {m.escalated
                      ? <Chip size="small" label="Escaladat" color="error" variant="outlined" />
                      : m.overdue
                        ? <Chip size="small" label="Restant" color="warning" variant="outlined" />
                        : <Chip size="small" label="Apropiat" color="info" variant="outlined" />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Typography variant="subtitle2">Ordine pe utilaj</Typography>
      {byEquipment.map(([equipmentName, rows]) => (
        <Paper key={equipmentName} variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{equipmentName}</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nr.</TableCell><TableCell>Produs</TableCell><TableCell>Start</TableCell><TableCell>Final</TableCell><TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((o) => (
                <TableRow key={o.id}>
                  <TableCell><code>{o.number}</code></TableCell>
                  <TableCell>{o.productArticleCode} — {o.productArticleDescription}</TableCell>
                  <TableCell>{o.plannedStartDate}</TableCell>
                  <TableCell>{o.plannedEndDate}</TableCell>
                  <TableCell><Chip size="small" label={STATUS_LABEL[o.status]} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ))}
      {byEquipment.length === 0 && <Box sx={{ color: 'text.secondary' }}>Niciun ordin planificat sau lansat.</Box>}
    </Stack>
  )
}
